import React, { useState, useEffect, useMemo } from 'react';
import { Calculator, RotateCcw, TrendingUp } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const LoanInputForm = ({ onCalculate, onClear, isCalculating }) => {
  // Always call hooks at the top level - never conditionally
  const [formData, setFormData] = useState({
    purchasePrice: '',
    downPayment: '',
    loanAmount: '',
    numberOfPayments: '',
    annualInterestRate: '',
    paymentAmount: '',
    loanClosingDate: new Date()?.toISOString()?.split('T')?.[0],
    firstPaymentDate: '',
    paymentFrequency: 'monthly',
    points: '',
    loanDescription: ''
  });

  const [errors, setErrors] = useState({});
  const [solveFor, setSolveFor] = useState('paymentAmount');

  // Memoize payment frequency options - this was likely causing the hook violation
  const paymentFrequencyOptions = useMemo(() => [
    { value: 'monthly', label: 'Monthly' },
    { value: 'bi-weekly', label: 'Bi-Weekly (26 payments/year)' },
    { value: 'semi-monthly', label: 'Semi-Monthly (24 payments/year)' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'annually', label: 'Annually' }
  ], []);

  // Memoize solve for options
  const solveForOptions = useMemo(() => [
    { value: 'loanAmount', label: 'Loan Amount' },
    { value: 'numberOfPayments', label: 'Number of Payments' },
    { value: 'annualInterestRate', label: 'Interest Rate' },
    { value: 'paymentAmount', label: 'Payment Amount' }
  ], []);

  // Always call useEffect at the same position
  useEffect(() => {
    const nextMonth = new Date();
    nextMonth?.setMonth(nextMonth?.getMonth() + 1);
    nextMonth?.setDate(1);
    
    // Try to restore form data from localStorage first
    const savedFormData = localStorage.getItem('currentLoanFormData');
    if (savedFormData) {
      try {
        const parsedFormData = JSON.parse(savedFormData);
        setFormData(parsedFormData);
        return; // Exit early if we restored data
      } catch (error) {
        console.warn('Error parsing saved form data:', error);
      }
    }
    
    // Only set default first payment date if no saved data
    setFormData(prev => ({
      ...prev,
      firstPaymentDate: nextMonth?.toISOString()?.split('T')?.[0]
    }));
  }, []);

  const handleInputChange = (field, value) => {
    // Special handling for interest rate to preserve precision
    if (field === 'annualInterestRate') {
      // Allow up to 4 decimal places and preserve the raw input
      const numericValue = value?.replace(/[^\d.-]/g, ''); // Remove non-numeric characters except decimal and minus
      const decimalParts = numericValue?.split('.');
      
      if (decimalParts?.length <= 2) {
        // Limit to 4 decimal places
        if (decimalParts?.[1] && decimalParts?.[1]?.length > 4) {
          decimalParts[1] = decimalParts?.[1]?.substring(0, 4);
        }
        value = decimalParts?.join('.');
      } else {
        // Invalid input with multiple decimals, keep previous value
        return;
      }
    }

    setFormData(prev => {
      const newFormData = {
        ...prev,
        [field]: value
      };
      
      // Auto-calculate loan amount when purchase price or down payment changes
      if (field === 'purchasePrice' || field === 'downPayment') {
        const purchasePrice = parseFloat(field === 'purchasePrice' ? value : prev?.purchasePrice) || 0;
        const downPayment = parseFloat(field === 'downPayment' ? value : prev?.downPayment) || 0;
        
        if (purchasePrice > 0) {
          const calculatedLoanAmount = Math.max(0, purchasePrice - downPayment);
          newFormData.loanAmount = calculatedLoanAmount?.toString();
        }
      }
      
      return newFormData;
    });
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate required fields based on what we're solving for
    const requiredFields = ['loanAmount', 'numberOfPayments', 'annualInterestRate', 'paymentAmount']?.filter(field => field !== solveFor);
    
    requiredFields?.forEach(field => {
      if (!formData?.[field] || formData?.[field] === '') {
        newErrors[field] = 'This field is required';
      }
    });
    
    // Validate numeric fields
    const numericFields = ['purchasePrice', 'downPayment', 'loanAmount', 'numberOfPayments', 'annualInterestRate', 'paymentAmount', 'points'];
    numericFields?.forEach(field => {
      if (formData?.[field] && isNaN(parseFloat(formData?.[field]))) {
        newErrors[field] = 'Must be a valid number';
      }
    });
    
    // Validate positive values
    const positiveFields = ['purchasePrice', 'downPayment', 'loanAmount', 'numberOfPayments', 'paymentAmount'];
    positiveFields?.forEach(field => {
      if (formData?.[field] && parseFloat(formData?.[field]) < 0) {
        newErrors[field] = 'Must be 0 or greater';
      }
    });
    
    // Validate down payment doesn't exceed purchase price
    if (formData?.purchasePrice && formData?.downPayment) {
      const purchasePrice = parseFloat(formData?.purchasePrice);
      const downPayment = parseFloat(formData?.downPayment);
      
      if (downPayment > purchasePrice) {
        newErrors.downPayment = 'Down payment cannot exceed purchase price';
      }
    }
    
    // Validate interest rate range
    if (formData?.annualInterestRate && (parseFloat(formData?.annualInterestRate) < 0 || parseFloat(formData?.annualInterestRate) > 100)) {
      newErrors.annualInterestRate = 'Interest rate must be between 0 and 100';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      onCalculate(formData);
    }
  };

  const handleClear = () => {
    const clearedFormData = {
      purchasePrice: '',
      downPayment: '',
      loanAmount: '',
      numberOfPayments: '',
      annualInterestRate: '',
      paymentAmount: '',
      loanClosingDate: new Date()?.toISOString()?.split('T')?.[0],
      firstPaymentDate: '',
      paymentFrequency: 'monthly',
      points: '',
      loanDescription: ''
    };
    
    setFormData(clearedFormData);
    setErrors({});
    setSolveFor('paymentAmount');
    
    // Clear saved form data from localStorage
    localStorage.removeItem('currentLoanFormData');
    localStorage.removeItem('currentLoanCalculation');
    
    onClear();
  };

  return (
    <div className="card-professional-elevated">
      <div className="card-professional-header">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-text-primary">Loan Configuration</h2>
            <p className="text-sm text-text-secondary">Enter loan details to calculate payments</p>
          </div>
        </div>
      </div>
      <div className="card-professional-content">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Loan Description */}
          <div className="form-group">
            <Input
              label="Loan Description"
              type="text"
              value={formData?.loanDescription}
              onChange={(e) => handleInputChange('loanDescription', e?.target?.value)}
              error={errors?.loanDescription}
              placeholder="e.g., Home Mortgage, Auto Loan, Personal Loan"
              className="input-professional"
            />
          </div>

          {/* Purchase Information Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-text-primary">Purchase Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Purchase Price */}
              <div className="form-group">
                <Input
                  label="Purchase Price ($)"
                  type="number"
                  value={formData?.purchasePrice}
                  onChange={(e) => handleInputChange('purchasePrice', e?.target?.value)}
                  error={errors?.purchasePrice}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="input-professional"
                />
                <p className="form-helper">
                  Total price of the item being financed
                </p>
              </div>

              {/* Down Payment */}
              <div className="form-group">
                <Input
                  label="Down Payment ($)"
                  type="number"
                  value={formData?.downPayment}
                  onChange={(e) => handleInputChange('downPayment', e?.target?.value)}
                  error={errors?.downPayment}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="input-professional"
                />
                <p className="form-helper">
                  Amount paid upfront (reduces loan amount)
                </p>
              </div>
            </div>

            {/* Calculated Loan Amount Display */}
            {formData?.purchasePrice && (
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-text-secondary">Calculated Loan Amount:</span>
                  <span className="text-lg font-bold text-primary">
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    })?.format(parseFloat(formData?.loanAmount) || 0)}
                  </span>
                </div>
                <p className="text-xs text-text-secondary mt-1">
                  Purchase Price - Down Payment = Loan Amount
                </p>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="divider-professional"></div>

          {/* Solve For Selection */}
          <div className="form-group">
            <label className="form-label">
              Calculate Missing Value
            </label>
            <Select
              value={solveFor}
              onChange={(value) => {
                setSolveFor(value);
                setErrors({}); // Clear errors when changing solve for
              }}
              options={solveForOptions}
              placeholder="Select what to calculate"
              className="select-professional"
            />
            <p className="form-helper">
              Select which value you want the calculator to determine
            </p>
          </div>

          {/* Financial Parameters Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Loan Amount - Now read-only when purchase price is entered */}
            <div className="form-group">
              <Input
                label="Loan Amount ($)"
                type="number"
                value={formData?.loanAmount}
                onChange={(e) => handleInputChange('loanAmount', e?.target?.value)}
                error={errors?.loanAmount}
                disabled={solveFor === 'loanAmount' || formData?.purchasePrice}
                placeholder="0.00"
                min="0"
                step="0.01"
                className={`input-professional ${
                  solveFor === 'loanAmount' || formData?.purchasePrice 
                    ? 'bg-primary/5 border-primary/30' :''
                }`}
              />
              {formData?.purchasePrice && (
                <p className="form-helper text-primary">
                  Auto-calculated from purchase price and down payment
                </p>
              )}
            </div>

            {/* Number of Payments */}
            <div className="form-group">
              <Input
                label="Number of Payments"
                type="number"
                value={formData?.numberOfPayments}
                onChange={(e) => handleInputChange('numberOfPayments', e?.target?.value)}
                error={errors?.numberOfPayments}
                disabled={solveFor === 'numberOfPayments'}
                placeholder="0"
                min="1"
                step="1"
                className={`input-professional ${solveFor === 'numberOfPayments' ? 'bg-primary/5 border-primary/30' : ''}`}
              />
            </div>

            {/* Annual Interest Rate */}
            <div className="form-group">
              <Input
                label="Annual Interest Rate (%)"
                type="text"
                value={formData?.annualInterestRate}
                onChange={(e) => handleInputChange('annualInterestRate', e?.target?.value)}
                error={errors?.annualInterestRate}
                disabled={solveFor === 'annualInterestRate'}
                placeholder="0.0000"
                className={`input-professional ${solveFor === 'annualInterestRate' ? 'bg-primary/5 border-primary/30' : ''}`}
                inputMode="decimal"
                pattern="[0-9]*\.?[0-9]{0,4}"
              />
              <p className="form-helper">
                Interest rate with 4 decimal place precision (e.g., 4.9969%)
              </p>
            </div>

            {/* Payment Amount */}
            <div className="form-group">
              <Input
                label="Payment Amount ($)"
                type="number"
                value={formData?.paymentAmount}
                onChange={(e) => handleInputChange('paymentAmount', e?.target?.value)}
                error={errors?.paymentAmount}
                disabled={solveFor === 'paymentAmount'}
                placeholder="0.00"
                min="0"
                step="0.01"
                className={`input-professional ${solveFor === 'paymentAmount' ? 'bg-primary/5 border-primary/30' : ''}`}
              />
            </div>
          </div>

          {/* Divider */}
          <div className="divider-professional"></div>

          {/* Loan Timeline */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-text-primary">Loan Timeline</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Loan Closing Date */}
              <div className="form-group">
                <Input
                  label="Loan Closing Date"
                  type="date"
                  value={formData?.loanClosingDate}
                  onChange={(e) => handleInputChange('loanClosingDate', e?.target?.value)}
                  error={errors?.loanClosingDate}
                  className="input-professional"
                />
              </div>

              {/* First Payment Date */}
              <div className="form-group">
                <Input
                  label="First Payment Date"
                  type="date"
                  value={formData?.firstPaymentDate}
                  onChange={(e) => handleInputChange('firstPaymentDate', e?.target?.value)}
                  error={errors?.firstPaymentDate}
                  className="input-professional"
                />
              </div>
            </div>

            {/* Payment Frequency */}
            <div className="form-group">
              <label className="form-label">
                Payment Frequency
              </label>
              <Select
                value={formData?.paymentFrequency}
                onChange={(value) => handleInputChange('paymentFrequency', value)}
                options={paymentFrequencyOptions}
                placeholder="Select payment frequency"
                className="select-professional"
              />
            </div>
          </div>

          {/* Additional Options */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-text-primary">Additional Options</h3>
            
            {/* Points */}
            <div className="form-group">
              <Input
                label="Points (%)"
                type="number"
                value={formData?.points}
                onChange={(e) => handleInputChange('points', e?.target?.value)}
                error={errors?.points}
                placeholder="0.00"
                min="0"
                step="0.01"
                className="input-professional"
              />
              <p className="form-helper">
                Points paid at closing (optional)
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-6">
            <Button
              type="submit"
              variant="primary"
              disabled={isCalculating}
              className="btn-professional btn-primary flex-1"
            >
              <Calculator className="w-4 h-4" />
              {isCalculating ? 'Calculating...' : 'Calculate Loan'}
            </Button>
            
            <Button
              type="button"
              variant="secondary"
              onClick={handleClear}
              disabled={isCalculating}
              className="btn-professional btn-secondary"
            >
              <RotateCcw className="w-4 h-4" />
              Clear
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoanInputForm;